<template>
  <SFReducedHeader v-if="isReduced" />
  <header
    v-else
    class="sticky left-0 top-0 z-50 mx-auto w-full max-w-screen-2xl border-b border-gray-100 bg-white xs:px-4 xs:py-2.5 md:gap-x-8 md:px-6 lg:relative lg:z-auto lg:grid lg:w-full lg:grid-cols-[auto_1fr_auto] lg:items-center lg:border-b lg:bg-transparent lg:py-0"
    data-testid="header"
  >
    <!-- Left: Logo -->
    <div class="hidden items-center justify-start md:pb-8 md:pt-6 lg:flex">
      <SFLogoLink data-testid="header-logo" class="h-auto max-w-[131px]" />
    </div>

    <!-- Center: Menus -->
    <div class="!m-0 hidden flex-col items-center pt-5 md:gap-y-6 lg:flex">
      <!-- Top Navigation -->
      <SFHeaderTopNavigation />
      <!-- Main Navigation -->
      <nav class="!m-0" data-testid="nav-categories">
        <ul class="flex items-center">
          <SFHeaderMenuSkeleton v-if="!mainNavigationItems" />
          <template v-else>
            <SFHeaderNavigationItem
              v-for="item in mainNavigationItems"
              :key="item.id"
              :item="item"
              :is-first-item="item.id === firstItemFromNavigationTree?.id"
              class="pb-4 text-sm font-medium hover:text-anthracite-800"
            />
          </template>
        </ul>
      </nav>
    </div>

    <div class="hidden items-center justify-end space-x-6 lg:flex">
      <SFSearchInput id="search-desktop" ref="searchInput" />
    </div>

    <!-- Mobile Header -->
    <SFMobileHeader
      v-model:is-side-navigation-open="isSideNavigationOpen"
      :main-navigation-items="mainNavigationItems || []"
    />
  </header>
</template>

<script setup lang="ts">
import { useElementHover } from '@vueuse/core'
import { computed, useTemplateRef, watch } from 'vue'
import type {
  NavigationItems,
  NavigationTreeItem,
} from '@scayle/storefront-nuxt'
import SFLogoLink from '../../SFLogoLink.vue'
import SFSearchInput from './search/SFSearchInput.vue'
import SFHeaderNavigationItem from './navigation/SFHeaderNavigationItem.vue'
import SFHeaderTopNavigation from './navigation/SFHeaderTopNavigation.vue'
import SFMobileHeader from './SFMobileHeader.vue'
import SFHeaderMenuSkeleton from './navigation/SFHeaderMenuSkeleton.vue'
import SFReducedHeader from './SFReducedHeader.vue'

import { useDefaultBreakpoints } from '~/modules/ui/runtime'
import { useTopNavigationMenu } from '~/composables/useTopNavigationMenu'
import {
  IGNORED_NAVIGATION_IDS,
  MENU_NAVIGATION_NAME,
  NAVIGATION_ID_MAP,
} from '~/constants'

import { useCMSBySlug } from '~/modules/cms/providers/contentful/composables/useCMS'
import type { TypeSearchSuggestionsSkeleton } from '~/modules/cms/providers/contentful/types'
import { useNuxtData } from '#app'
import { useShopGenderCookie } from '~/composables/useShopGenderCookie'
import {
  useCurrentShop,
  useRpc,
  useAvailableShops,
} from '#storefront/composables'

const searchInput = useTemplateRef<HTMLDivElement>('searchInput')

const isHovered = useElementHover(searchInput)

const { cookie } = useShopGenderCookie()

const searchSuggestionsSlug = computed(() => {
  const _cookie = cookie.value.toLowerCase()

  if (_cookie) {
    return `search-suggestions-${_cookie}`
  }
  return 'search-suggestions'
})

const currentShop = useCurrentShop()
const availableShops = useAvailableShops()

const currentShopLocale = computed(() => {
  return (
    availableShops.value.find(
      (shop) => shop.locale === currentShop.value.locale,
    )?.locale ?? 'en-GB'
  )
})

const { execute: getSearchSuggestionsCMS } =
  await useCMSBySlug<TypeSearchSuggestionsSkeleton>(
    `${searchSuggestionsSlug.value}`,
    {
      'fields.slug': `${searchSuggestionsSlug.value}`,
      content_type: 'searchSuggestions',
      // @ts-expect-error wrong type definition in useCMSBySlug from core
      locale: currentShopLocale.value,
    },
    {
      watch: [searchSuggestionsSlug],
      immediate: false,
    },
  )

const { data: searchSuggestionsCMSData } = useNuxtData(
  `${searchSuggestionsSlug.value}`,
)

watch(isHovered, async (val) => {
  if (!val) {
    return
  }
  if (searchSuggestionsCMSData.value) {
    return
  }
  await getSearchSuggestionsCMS()
})

const isSideNavigationOpen = defineModel('isMobileSidebarOpen', {
  type: Boolean,
  default: false,
})

const isReduced = defineModel('reduced', {
  type: Boolean,
  default: false,
})

const { currentMenu } = useTopNavigationMenu()

const menuName = computed(() => currentMenu.value || '')

const navigationQuery = computed(() =>
  `${MENU_NAVIGATION_NAME} ${currentShop.value.countryCode} ${menuName.value}`.trim(),
)

const { greaterOrEqual } = useDefaultBreakpoints()
const isDesktopLayout = greaterOrEqual('lg')

const filterNavigationItems = (items: NavigationItems): NavigationItems => {
  const date = new Date()

  return items?.filter((item: NavigationTreeItem) => {
    if (item.visibleFrom && new Date(item.visibleFrom) > date) {
      return false
    }

    return !(item.visibleTo && new Date(item.visibleTo) < date)
  })
}

const transformNavigationItem = (
  item: NavigationTreeItem,
): NavigationTreeItem => {
  return {
    ...item,
    children: filterNavigationItems(item.children || []).map(
      transformNavigationItem,
    ),
  }
}

const navigationParams = computed(() => {
  return {
    navigationId: menuName.value
      ? NAVIGATION_ID_MAP[navigationQuery.value]
      : '',
    params: {
      with: { category: true },
    },
  }
})

const { data: navigationData } = await useRpc(
  'getNavigationTreeById' as any,
  `header-navigation-${menuName.value}`,
  navigationParams,
)

const mainNavigationItems = computed(
  () =>
    filterNavigationItems(
      navigationData.value?.items?.filter(
        (item: NavigationTreeItem) =>
          !IGNORED_NAVIGATION_IDS.includes(
            item?.id as (typeof IGNORED_NAVIGATION_IDS)[number],
          ),
      ),
    )?.map(transformNavigationItem) || [],
)

const firstItemFromNavigationTree = computed(() => {
  return mainNavigationItems.value?.[0]
})

watch(
  isDesktopLayout,
  (val) => {
    if (val) {
      isSideNavigationOpen.value = false
    } else {
      if (searchSuggestionsCMSData.value) {
        return
      }
      getSearchSuggestionsCMS()
    }
  },
  { immediate: true },
)
</script>
